import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fileApi } from '@/api/endpoints';
import type { AttachedMedia, MediaType } from '@/api/types';
import { useAuthStore } from '@/store/authStore';
import {
  MEDIA_ACCEPT,
  createDraft,
  detectMediaType,
  formatBytes,
  releaseDraft,
  type DraftMedia,
} from '@/utils/media';

/**
 * 썸네일 한 칸. 이미지는 썸네일(없으면 원본), 동영상은 썸네일 위에 재생 표시를 얹는다.
 * 누르면 원본을 새 탭에서 연다. 동영상은 브라우저 기본 플레이어로 재생된다.
 */
export function MediaTile({
  mediaType,
  url,
  thumbnailUrl,
  size = 88,
}: {
  mediaType: MediaType;
  url: string;
  thumbnailUrl?: string;
  size?: number;
}) {
  const src = thumbnailUrl ?? (mediaType === 'IMAGE' ? url : undefined);
  return (
    <a className="media-tile" href={url} target="_blank" rel="noreferrer" style={{ width: size, height: size }}>
      {src ? (
        <img src={src} alt={mediaType === 'VIDEO' ? '첨부 동영상' : '첨부 이미지'} />
      ) : (
        <span className="media-tile__placeholder">동영상</span>
      )}
      {mediaType === 'VIDEO' && <span className="media-tile__play" aria-hidden="true" />}
    </a>
  );
}

/** 저장된 첨부를 나란히 보여 준다. 리뷰·댓글·게시글 어디서나 같은 크기로 쓴다. */
export function MediaThumbs({ media, size = 88 }: { media: AttachedMedia[]; size?: number }) {
  if (media.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {media.map((item) => (
        <MediaTile key={item.mediaId} {...item} size={size} />
      ))}
    </div>
  );
}

interface Props {
  value: DraftMedia[];
  onChange: (media: DraftMedia[]) => void;
  max?: number;
  label?: string;
  /** 저장 중 업로드 진행률(0~100). 값이 있으면 입력을 잠근다. useMediaUpload 의 progress 를 그대로 넘긴다. */
  progress?: number | null;
  size?: number;
}

/**
 * 이미지·동영상 첨부 입력.
 * 파일을 고르면 브라우저 안에서 미리보기와 400px 썸네일만 만들어 두고,
 * 실제 S3 업로드는 부모가 저장 버튼을 누를 때 useMediaUpload 로 한다.
 */
export default function MediaAttach({ value, onChange, max = 5, label = '사진·동영상 첨부', progress, size = 88 }: Props) {
  const user = useAuthStore((s) => s.user);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploading = progress != null;
  const full = value.length >= max;

  const policy = useQuery({
    queryKey: ['uploadPolicy', user?.userId],
    queryFn: fileApi.policy,
    enabled: Boolean(user),
    staleTime: Infinity,
  });

  // 목록에서 빠진 첨부의 blob: URL 을 놓아준다. 제거·저장 후 초기화 모두 여기서 처리된다.
  const previous = useRef<DraftMedia[]>(value);
  useEffect(() => {
    const kept = new Set(value.map((draft) => draft.id));
    previous.current.filter((draft) => !kept.has(draft.id)).forEach(releaseDraft);
    previous.current = value;
  }, [value]);

  async function addFiles(files: File[]) {
    setError(null);
    const room = max - value.length;
    if (files.length > room) {
      setError(`최대 ${max}개까지 첨부할 수 있습니다.`);
    }
    const accepted: { file: File; mediaType: MediaType }[] = [];
    for (const file of files.slice(0, room)) {
      const mediaType = detectMediaType(file);
      if (!mediaType) {
        setError(`${file.name}: 이미지나 동영상(mp4, m4v, webm, mov)만 첨부할 수 있습니다.`);
        continue;
      }
      const limit = mediaType === 'VIDEO' ? policy.data?.maxVideoBytes : policy.data?.maxImageBytes;
      if (limit && file.size > limit) {
        setError(`${file.name}: 파일이 너무 큽니다. (최대 ${formatBytes(limit)})`);
        continue;
      }
      accepted.push({ file, mediaType });
    }
    if (accepted.length === 0) return;

    setPreparing(true);
    try {
      const drafts = await Promise.all(accepted.map(({ file, mediaType }) => createDraft(file, mediaType)));
      onChange([...value, ...drafts]);
    } finally {
      setPreparing(false);
    }
  }

  return (
    <div className="field">
      <label>
        {label} (최대 {max}개)
      </label>

      {value.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {value.map((draft) => (
            <div key={draft.id} style={{ width: size }}>
              <MediaTile
                mediaType={draft.mediaType}
                url={draft.previewUrl}
                thumbnailUrl={draft.posterUrl}
                size={size}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm btn--block"
                disabled={uploading}
                onClick={() => onChange(value.filter((item) => item.id !== draft.id))}
              >
                제거
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        accept={MEDIA_ACCEPT}
        multiple
        disabled={uploading || preparing || full}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) void addFiles(files);
          e.target.value = '';
        }}
      />
      {preparing && <span className="muted">미리보기를 준비하는 중입니다…</span>}
      {uploading && <span className="muted">파일 업로드 중… {progress}%</span>}
      {full && !uploading && <span className="muted">더 이상 첨부할 수 없습니다.</span>}
      {error && <span className="field__error">{error}</span>}
    </div>
  );
}
